import React, { useState, useEffect, useContext } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  ACCOUNT_TABLE,
  AUDIT,
  CASH_FLOW_DASHBOARD,
  CONTENT,
  COORDINATORS_DASHBOARD,
  DIRECTORS_DASHBOARD,
  PPC,
  PROFITABILITY_DASHBOARD,
  SALES_STATS,
  SEO,
  SOCIAL_MEDIA,
  SOCIAL_PLUS,
  TEAMS,
  VPS_DASHBOARD,
  WEBSITE,
  SALES_LIFECYCLE_DASHBOARD,
  WEBSITE_DASHBOARD,
  GARGLE_CHAT_WIDGET,
  TASKS_VIEW,
  TICKETS_VIEW,
  WEBSITE_TEAM_VIEW,
} from "./items";

import "./Sidebar.css";
import { getStorageValue, setStorageValue } from "../../Utils";
import { getSidebarParam } from "../../Utils";
import { getSalesRep } from "../../SF";
import { Alert } from "react-bootstrap";
import { logout } from "../../API";
import { useAuthContext } from "../../../contexts/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChartLine,
  faTv,
  faChartPie,
  faInfoCircle,
  faUser,
  faUserTie,
  faUsers,
  faGlobe,
  faSync,
  faMoneyBillWave,
  faDownload,
  faComments,
  faBullhorn,
  faShareAlt,
  faClipboardList,
  faFileAlt,
  faSignOutAlt,
  faCogs,
  faArrowDownShortWide,
  faTasks,
  faTicketAlt,
} from "@fortawesome/free-solid-svg-icons";
import { useSidebarItems } from "./items";
import { AuthContext } from "../../../contexts/AuthContext";

const Sidebar = () => {
  const { loading, user } = useAuthContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const location = useLocation();
  const items = useSidebarItems();

  // Apply body classes based on sidebar state
  useEffect(() => {
    document.body.classList.remove("sidebar-expanded", "sidebar-contracted");
    document.body.classList.add(
      isExpanded ? "sidebar-expanded" : "sidebar-contracted"
    );

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("sidebar-expanded", "sidebar-contracted");
    };
  }, [isExpanded]);

  // Set initial body class on mount
  useEffect(() => {
    document.body.classList.add("sidebar-contracted");

    return () => {
      document.body.classList.remove("sidebar-expanded", "sidebar-contracted");
    };
  }, []);

  if (loading) return <div>Loading...</div>;

  // Extract the actual user data from the nested structure
  const actualUser = user?.user || user;

  const is_employee =
    actualUser &&
    (actualUser.userRole === "admin" || actualUser.userRole === "user");
  const is_employee_admin = actualUser && actualUser.userRole === "admin";
  const is_api = actualUser && actualUser.userRole === "api";

  const growthRoutes = [
    { path: "/salesFlowDashboard", label: "Sales Flow", icon: faChartLine },
    {
      path: "/salesLifecycleDashboard",
      label: "Sales Lifecycle",
      icon: faSync,
    },
    { path: "/cashFlow", label: "Cash Flow", icon: faMoneyBillWave },
  ];

  const viewRoutes = [
    { path: "/teams", label: "Teams", icon: faUsers },
    { path: "/socialplus", label: "Social+", icon: faComments },
    { path: "/website", label: "Website", icon: faGlobe },
    { path: "/ppc", label: "PPC", icon: faBullhorn },
    { path: "/seo", label: "SEO", icon: faSearch },
    { path: "/socialmedia", label: "Social Media", icon: faShareAlt },
    { path: "/audit", label: "Account Audit", icon: faClipboardList },
    { path: "/content", label: "Content", icon: faFileAlt },
  ];

  const managementRoutes = [
    { path: "/tickets", label: "Tickets", icon: faTicketAlt },
    { path: "/tasks", label: "Tasks", icon: faTasks },
    { path: "/website-team", label: "Website Team", icon: faGlobe },
  ];

  // Debug: Log user structure if needed
  // console.log("----> Sidebar user:", user);

  if (!actualUser) {
    return (
      <div className="sidebar-container expanded">
        <div className="sidebar">
          <div className="menu-title menu-name">Hello!</div>
          <NavLink
            to="/login"
            title="Authenticate to view all available menu options"
            className="sidebar-link"
          >
            <FontAwesomeIcon icon={faUser} title="Authenticate" />
            <span>Authenticate</span>
            <FontAwesomeIcon
              icon={faInfoCircle}
              title="Info"
              style={{ marginLeft: "5px", fontSize: "10px", opacity: "0.5" }}
            />
          </NavLink>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`sidebar-container${isExpanded ? " expanded" : " contracted"}`}
      onMouseEnter={() => setIsExpanded(true)}
      onMouseLeave={() => setIsExpanded(false)}
    >
      <div className="sidebar">
        <div className="menu-title menu-name">
          Hello, {actualUser.userFullName || actualUser.userId}
        </div>
        {is_employee && (
          <>
            <div className="menu-title">Accountability</div>
            <NavLink
              to="/directorsDashboard"
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <FontAwesomeIcon icon={faUserTie} />
              <span>Directors</span>
            </NavLink>
            <NavLink
              to="/coordinatorsDashboard"
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <FontAwesomeIcon icon={faUsers} />
              <span>Coordinators</span>
            </NavLink>
            <NavLink
              to="/fulfillmentDashboard"
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <FontAwesomeIcon icon={faTasks} />
              <span>Fulfillment</span>
            </NavLink>
            <NavLink
              to="/salesstats"
              className={({ isActive }) => (isActive ? "is-active" : "")}
            >
              <FontAwesomeIcon icon={faTv} />
              <span>TV Sales Stats</span>
            </NavLink>
            <div className="menu-title">Reports</div>
            <a
              href="https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZRo000002bhKzMAI/view"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <FontAwesomeIcon icon={faChartLine} />
              <span>Sales Insights</span>
            </a>
            <a
              href="https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZRo000002f6ebMAA/view?queryScope=userFolders"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <FontAwesomeIcon icon={faBullhorn} />
              <span>Marketing Insights</span>
            </a>
            <a
              href="https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057vO2AQ/view?queryScope=userFolders"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <FontAwesomeIcon icon={faArrowDownShortWide} />
              <span>Directors Upsell</span>
            </a>
            <a
              href="https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057so2AA/view?queryScope=userFolders"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <FontAwesomeIcon icon={faCogs} />
              <span>Engineering Dept. Performance</span>
            </a>
            <a
              href="https://gargleinc.lightning.force.com/lightning/r/Dashboard/01ZEY00000057t32AA/view?queryScope=userFolders"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
            >
              <FontAwesomeIcon icon={faUser} />
              <span>Process & HR Dept. Performance</span>
            </a>
          </>
        )}
        {is_employee && (
          <>
            <div className="menu-title">Management</div>
            {managementRoutes.map(({ path, label, icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) => (isActive ? "is-active" : "")}
              >
                <FontAwesomeIcon icon={icon} />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
        {is_employee_admin && (
          <>
            <div className="menu-title">Growth</div>
            {growthRoutes.map(({ path, label, icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) => (isActive ? "is-active" : "")}
              >
                <FontAwesomeIcon icon={icon} />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
        {is_employee && (
          <>
            <div className="menu-title">Views</div>
            {viewRoutes.map(({ path, label, icon }) => (
              <NavLink
                key={path}
                to={path}
                className={({ isActive }) => (isActive ? "is-active" : "")}
              >
                <FontAwesomeIcon icon={icon} />
                <span>{label}</span>
              </NavLink>
            ))}
          </>
        )}
        {is_api && (
          <NavLink
            to="/login"
            title="Authenticate to view all available menu options"
            className={({ isActive }) => (isActive ? "is-active" : "")}
          >
            <FontAwesomeIcon icon={faUser} />
            <span>Authenticate</span>
            <FontAwesomeIcon
              icon={faInfoCircle}
              style={{ marginLeft: "5px", fontSize: "10px", opacity: "0.5" }}
            />
          </NavLink>
        )}
        {!is_api && (
          <NavLink
            to="/logout"
            className={({ isActive }) => (isActive ? "is-active" : "")}
          >
            <FontAwesomeIcon icon={faSignOutAlt} />
            <span>Logout</span>
          </NavLink>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
